import {inject, Injectable} from './di';
import {ConfigService} from './config/config-service';
import {BehaviorSubject} from 'rxjs';
import {AppConfig} from './config/app-config';
import {REGION_URLS_MAP} from './config/region-urls-map';
import {HotelRegion} from './config/hotel-region';
import {HotelInfoService} from './hotel-info-service';
import {Logger} from './logger';
import {AppInitializeService} from './app-initialize-service';

/**
 * Service for handling URLs, APIs and other resource or service locations
 */
@Injectable()
export class UrlsService implements AppInitializeService {
  private readonly configService = inject(ConfigService);
  private readonly hotelInfoService = inject(HotelInfoService);
  private readonly logger = inject(Logger);

  public readonly serverUrl$ = new BehaviorSubject<string>('');
  public readonly apiUrl$ = new BehaviorSubject<string>('');
  public readonly tvApiUrl$ = new BehaviorSubject<string>('');
  public readonly resUrl$ = new BehaviorSubject<string>('');
  public readonly dateTimeUrl$ = new BehaviorSubject<string>('');

  public get serverUrl() {
    return this.serverUrl$.getValue();
  }

  public get apiUrl() {
    return this.apiUrl$.getValue();
  }

  public get tvApiUrl() {
    return this.tvApiUrl$.getValue();
  }

  public get resUrl() {
    return this.resUrl$.getValue();
  }

  public get dateTimeUrl() {
    return this.dateTimeUrl$.getValue();
  }

  public async init(): Promise<boolean> {
    try {
      this.fixConfigUrls();

      this.initServerUrl();
      this.initAdminUrls();

      this.logger.log('[App] Server url: ' + this.serverUrl$.getValue());
      this.logger.log('[App] API url: ' + this.apiUrl$.getValue());
      this.logger.log('[App] TV API url: ' + this.tvApiUrl$.getValue());
      this.logger.log('[App] Resources url: ' + this.resUrl$.getValue());

      type ConfigUrlNames<T extends keyof AppConfig = keyof AppConfig> = T extends `${infer URL}_url`
        ? `${URL}`
        : never;

      const config = this.configService.config;
      (['admin', 'static', 'queue', 'remotedebug', 'chili', 'stat'] as ConfigUrlNames[]).forEach((urlCfg) =>
        this.logger.log(`[App] Using ${urlCfg} url: ${config[`${urlCfg}_url`]}'`),
      );

      this.logger.debug('[UrlsService] Application URLs initialized successfully');
    } catch (err) {
      this.logger.critical('[UrlsService] Unable to initialize application URLs', err);
      return Promise.resolve(false);
    }

    return Promise.resolve(true);
  }

  private fixConfigUrls() {
    const config = this.configService.config;
    const cfgPatch: Record<string, unknown> = {};

    const urlNames: Array<keyof (typeof REGION_URLS_MAP)[HotelRegion]> = [
      'admin_url',
      'static_url',
      'queue_url',
      'remotedebug_url',
      'chili_url',
      'stat_url',
    ];
    urlNames.forEach((url) => {
      cfgPatch[url] = this.getUrl(config, url);
    });
    cfgPatch.remoteStateUrl = REGION_URLS_MAP[this.hotelInfoService.region].remoteStateUrl;
    const eventBridgeUrl = config.eventBridge?.url || REGION_URLS_MAP[this.hotelInfoService.region].eventBridgeUrl;
    cfgPatch.eventBridge = {...config.eventBridge, url: eventBridgeUrl};

    this.configService.applyConfig(cfgPatch as unknown as AppConfig);
  }

  private getUrl(config: AppConfig, url: keyof (typeof REGION_URLS_MAP)[HotelRegion]): string {
    // TODO: 'custom' region
    // TODO: реализовать определение auto ssl
    const protocol = `http${config.use_ssl ? 's' : ''}://`;
    let out = config[url];

    if (out) {
      if (out.match(/^\/\//)) {
        out = protocol + out.slice(2);
      }
    } else {
      const region = this.hotelInfoService.region;
      out = REGION_URLS_MAP[region][url] + '/';
      if (region !== 'local') {
        out = protocol + out;
      }
    }

    return out;
  }

  private initServerUrl() {
    const baseUrl = document.baseURI || document.getElementsByTagName('base')[0]?.href || document.location.href;
    const baseUrlParts = baseUrl.split('/');
    if (baseUrlParts.length > 0) {
      baseUrlParts[baseUrlParts.length - 1] = '';
    }
    this.serverUrl$.next(baseUrlParts.join('/').toLowerCase());

    window.serverUrl = this.serverUrl$.getValue();
    window.getServerUrl = () => this.serverUrl$.getValue();
  }

  private initAdminUrls() {
    this.configService.config$.subscribe((config) => {
      if (!config) {
        return;
      }
      if (config.admin_url) {
        this.apiUrl$.next(config.admin_url + 'jsonapi/');
        this.tvApiUrl$.next(config.admin_url + 'tvconnect/');
        this.dateTimeUrl$.next(config.admin_url + 'datetime.php/');
        window.api_url = this.apiUrl$.getValue();
        window.tv_api_url = this.tvApiUrl$.getValue();
      }
      if (config.static_url) {
        this.resUrl$.next(config.static_url + 'files/');
        window.res_url = this.resUrl$.getValue();
      }
    });
  }
}
